/*
 * StackSchematic overlay: the account menu — an avatar at the foot of the
 * sidebar that opens who-you-are, "Manage profile", and "Sign out".
 *
 * WHY THIS EXISTS
 *
 * The scaffolded sidebar put a "Settings" group at the bottom whose icon was the
 * user avatar and whose only action was a link to /settings. There was no
 * discoverable way to SIGN OUT: Backstage's sign-out lives on a `⋮` menu inside
 * the profile card on the Settings page — two navigations and a corner menu deep.
 * When a session goes stale (a limited-user cookie the backend later rejects),
 * the only escape a person found was a fresh incognito window, because nothing in
 * the chrome let them drop their own session.
 *
 * So this replaces that group with a proper account menu: the avatar is a button,
 * and clicking it opens the signed-in identity plus the two things a person
 * actually wants from it — manage their profile, and sign out. It is the standard
 * top-of-mind "avatar → menu" pattern, placed where the avatar already was.
 *
 * WHY IT OWNS THE ROUTES IT LINKS TO
 *
 * The Settings and Visualizer pages the old group exposed (`page:user-settings`,
 * `page:app-visualizer`) are still real routes registered by their plugins; this
 * menu just links to them. apply.mjs `nav.take()`s them so they do not ALSO
 * appear in the sorted nav.rest() — taken for their side effect, rendered here.
 *
 * SIGN OUT IS THE REAL BACKSTAGE ONE
 *
 * `identityApi.signOut()` is exactly what Backstage's own UserSettingsMenu calls;
 * this is a more reachable entry point to it, not a reimplementation. A failure is
 * surfaced through the errorApi rather than swallowed, so a sign-out that cannot
 * complete says so instead of looking like it worked.
 */
import { type MouseEvent, useEffect, useState } from 'react';
import { SidebarItem } from '@backstage/core-components';
import {
  errorApiRef,
  identityApiRef,
  useApi,
  type ProfileInfo,
} from '@backstage/core-plugin-api';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import SignOutIcon from '@material-ui/icons/MeetingRoom';
import { useNavigate } from 'react-router-dom';

/** The avatar, sized for a sidebar item — the button that opens the account menu. */
const AvatarIcon = () => <UserSettingsSignInAvatar />;

export const UserMenuNavItem = () => {
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [profile, setProfile] = useState<ProfileInfo>({});

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getProfileInfo()
      .then(info => {
        if (!cancelled) setProfile(info);
      })
      // The menu still works without a profile — the avatar falls back to a
      // silhouette and the header to "Signed in". A failed lookup must not
      // remove the one control that lets a person sign out.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const close = () => setAnchorEl(null);
  const go = (to: string) => {
    close();
    navigate(to);
  };
  const signOut = () => {
    close();
    identityApi.signOut().catch(error => errorApi.post(error));
  };

  // Prefer a name, fall back to the email, then to a neutral label — never an
  // empty string, which reads as a broken row rather than a signed-in account.
  const label = profile.displayName || profile.email || 'Account';

  return (
    <>
      <SidebarItem
        icon={AvatarIcon}
        text={label}
        onClick={(ev: MouseEvent) => setAnchorEl(ev.currentTarget as HTMLElement)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        // The item sits at the foot of the left rail, so open the menu above it
        // and to the right rather than off the bottom of the viewport.
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box px={2} py={1}>
          <Typography variant="subtitle2" noWrap>
            {profile.displayName || 'Signed in'}
          </Typography>
          {profile.email && (
            <Typography variant="caption" color="textSecondary" noWrap component="div">
              {profile.email}
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => go('/settings')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => go('/app-visualizer')}>
          <ListItemIcon>
            <AccountTreeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Visualizer</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem data-testid="sign-out" onClick={signOut}>
          <ListItemIcon>
            <SignOutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
